import React, { useState, useEffect } from 'react';
import { consigneesAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const ConsigneeManagement = () => {
  const [consignees, setConsignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConsignee, setEditingConsignee] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    delivery_instructions: '',
    is_active: true,
  });

  useEffect(() => {
    loadConsignees();
  }, []);

  const loadConsignees = async () => {
    try {
      setLoading(true);
      const data = await consigneesAPI.getAll();
      setConsignees(data);
    } catch (error) {
      alert('Error loading consignees: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConsignee) {
        await consigneesAPI.update(editingConsignee.id, formData);
      } else {
        await consigneesAPI.create(formData);
      }
      setShowModal(false);
      setEditingConsignee(null);
      resetForm();
      loadConsignees();
    } catch (error) {
      alert('Error saving consignee: ' + error.message);
    }
  };

  const handleEdit = (consignee) => {
    setEditingConsignee(consignee);
    setFormData({
      company_name: consignee.company_name || '',
      contact_person: consignee.contact_person || '',
      email: consignee.email || '',
      phone: consignee.phone || '',
      address: consignee.address || '',
      city: consignee.city || '',
      state: consignee.state || '',
      zip_code: consignee.zip_code || '',
      country: consignee.country || 'USA',
      delivery_instructions: consignee.delivery_instructions || '',
      is_active: consignee.is_active !== undefined ? consignee.is_active : true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consignee?')) return;
    try {
      await consigneesAPI.delete(id);
      loadConsignees();
    } catch (error) {
      alert('Error deleting consignee: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      delivery_instructions: '',
      is_active: true,
    });
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'contact_person', label: 'Contact Person' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'is_active', label: 'Active', format: (val) => val ? 'Yes' : 'No' },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Consignee Management</h1>
        <button className="btn-primary" onClick={() => { setEditingConsignee(null); resetForm(); setShowModal(true); }}>
          Add New Consignee
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={consignees}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingConsignee(null); resetForm(); }}
        title={editingConsignee ? 'Edit Consignee' : 'Add New Consignee'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Company Name *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Contact Person</label>
              <input
                type="text"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="2"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Delivery Instructions</label>
            <textarea
              value={formData.delivery_instructions}
              onChange={(e) => setFormData({ ...formData, delivery_instructions: e.target.value })}
              rows="3"
            />
          </div>
          {editingConsignee && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingConsignee(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ConsigneeManagement;


